import { useState, useMemo } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import {
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  PieChart,
  AlertTriangle,
  CheckCircle,
  Building2,
  CreditCard,
  Banknote,
  Calculator,
  Loader2,
  Download,
  RefreshCw,
  XCircle,
  Info,
  TrendingUp,
} from "lucide-react";
import { formatChf } from "@shared/onboarding-types";
import { generateKPICSV, downloadCSV } from "@/lib/export-utils";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";

type Period = "month" | "quarter" | "year" | "ytd";
type SwissIndustry =
  | "manufacturing"
  | "retail"
  | "services"
  | "technology"
  | "finance"
  | "healthcare"
  | "construction"
  | "hospitality"
  | "transport"
  | "agriculture"
  | "real_estate"
  | "other";

// Chart colors using CSS variables
const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

// Calculate date ranges based on period selection
function getDateRange(period: Period): { startDate: number; endDate: number } {
  const now = new Date();
  const endDate = now.getTime();
  let startDate: number;

  switch (period) {
    case "month":
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
      break;
    case "quarter":
      const quarter = Math.floor(now.getMonth() / 3);
      startDate = new Date(now.getFullYear(), quarter * 3, 1).getTime();
      break;
    case "ytd":
      startDate = new Date(now.getFullYear(), 0, 1).getTime();
      break;
    case "year":
    default:
      startDate = new Date(
        now.getFullYear() - 1,
        now.getMonth(),
        now.getDate()
      ).getTime();
      break;
  }

  return { startDate, endDate };
}

// Map health status to color class
function getHealthColor(status: string): string {
  switch (status) {
    case "excellent":
    case "good":
      return "text-success";
    case "fair":
      return "text-warning";
    case "poor":
    case "critical":
      return "text-destructive";
    default:
      return "text-muted-foreground";
  }
}

// Map KPI status to color
function getStatusColor(status: string): string {
  switch (status) {
    case "good":
      return "text-success";
    case "warning":
      return "text-warning";
    case "critical":
      return "text-destructive";
    default:
      return "text-muted-foreground";
  }
}

// Map alert severity to badge variant
function getAlertBadgeVariant(
  severity: string
): "default" | "destructive" | "outline" | "secondary" {
  switch (severity) {
    case "critical":
      return "destructive";
    case "warning":
      return "outline";
    case "positive":
      return "secondary";
    default:
      return "outline";
  }
}

// Get icon for alert severity
function AlertIcon({ severity }: { severity: string }) {
  switch (severity) {
    case "critical":
      return <XCircle className="h-5 w-5 text-destructive mt-0.5" />;
    case "warning":
      return <AlertTriangle className="h-5 w-5 text-warning mt-0.5" />;
    case "positive":
      return <CheckCircle className="h-5 w-5 text-success mt-0.5" />;
    default:
      return <Info className="h-5 w-5 text-info mt-0.5" />;
  }
}

// Custom tooltip for charts
function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload) return null;

  return (
    <div className="bg-popover border rounded-lg shadow-lg p-3">
      <p className="font-medium mb-2">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {formatChf(entry.value * 100)}
        </p>
      ))}
    </div>
  );
}

// Custom pie chart label
function renderCustomizedLabel({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  name,
}: {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  percent?: number;
  name?: string;
}) {
  if (
    cx == null ||
    cy == null ||
    midAngle == null ||
    innerRadius == null ||
    outerRadius == null ||
    percent == null
  )
    return null;

  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 0.05) return null; // Don't show labels for small slices

  return (
    <text
      x={x}
      y={y}
      fill="hsl(var(--popover-foreground))"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      className="text-xs font-medium"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

export default function CFODashboard() {
  const { selectedCompanyId } = useCompany();
  const [period, setPeriod] = useState<Period>("ytd");
  const [industry, setIndustry] = useState<SwissIndustry>("services");

  // Calculate date range based on period
  const { startDate, endDate } = useMemo(() => getDateRange(period), [period]);

  // Query for company data
  const { data: company, isLoading: isLoadingCompany } =
    trpc.company.get.useQuery(
      { id: selectedCompanyId! },
      { enabled: !!selectedCompanyId }
    );

  // Query for KPIs from the CFO router
  const {
    data: kpiData,
    isLoading: isLoadingKPIs,
    refetch: refetchKPIs,
  } = trpc.cfo.getKPIs.useQuery(
    {
      companyId: selectedCompanyId!,
      industry,
      startDate,
      endDate,
    },
    {
      enabled: !!selectedCompanyId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  const isLoading = isLoadingCompany || isLoadingKPIs;

  // Extract key KPIs for the top cards
  const topKPIs = useMemo(() => {
    if (!kpiData?.kpis) return [];

    // Pick the most important KPIs for the top cards
    const importantKPIs = [
      "cash_runway",
      "quick_ratio",
      "net_margin",
      "current_ratio",
    ];
    return kpiData.kpis
      .filter(kpi => importantKPIs.includes(kpi.id))
      .slice(0, 4)
      .map(kpi => ({
        label: kpi.nameGerman || kpi.name,
        value: kpi.format,
        change: kpi.changeFromPrior
          ? Math.round(kpi.changeFromPrior * 100) / 100
          : 0,
        trend: kpi.trend as "up" | "down" | "stable",
        status: kpi.status as "good" | "warning" | "critical",
      }));
  }, [kpiData]);

  // Group KPIs by category for detailed view
  const kpisByCategory = useMemo(() => {
    if (!kpiData?.kpis) return {};

    return kpiData.kpis.reduce(
      (acc, kpi) => {
        const category = kpi.category || "other";
        if (!acc[category]) acc[category] = [];
        acc[category].push(kpi);
        return acc;
      },
      {} as Record<string, typeof kpiData.kpis>
    );
  }, [kpiData]);

  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <Card className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">No Company Selected</h2>
            <p className="text-muted-foreground">
              Please select a company to view the CFO Dashboard.
            </p>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const healthScore = kpiData?.healthScore ?? 0;
  const healthStatus = kpiData?.healthStatus ?? "fair";
  const alerts = kpiData?.alerts ?? [];
  const monthlyCashFlow = kpiData?.monthlyCashFlow ?? [];
  const expenseBreakdown = kpiData?.expenseBreakdown ?? [];
  const summary = kpiData?.summary;

  return (
    <DashboardLayout>
      <div className="container py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <BarChart3 className="h-8 w-8 text-primary" />
                CFO Dashboard
              </h1>
              <p className="text-muted-foreground mt-2">
                Financial health overview for {company?.name || "your company"}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Select
                value={industry}
                onValueChange={v => setIndustry(v as SwissIndustry)}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="services">Services</SelectItem>
                  <SelectItem value="technology">Technology</SelectItem>
                  <SelectItem value="manufacturing">Manufacturing</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="healthcare">Healthcare</SelectItem>
                  <SelectItem value="construction">Construction</SelectItem>
                  <SelectItem value="hospitality">Hospitality</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={period}
                onValueChange={v => setPeriod(v as Period)}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="quarter">This Quarter</SelectItem>
                  <SelectItem value="ytd">Year to Date</SelectItem>
                  <SelectItem value="year">Full Year</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetchKPIs()}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (kpiData?.kpis) {
                    const rows = kpiData.kpis.map(k => ({
                      label: k.name,
                      value: k.format,
                      change: k.changeFromPrior ?? 0,
                      status: k.trend,
                    }));
                    const csv = generateKPICSV(rows);
                    downloadCSV(
                      csv,
                      `cfo-kpis-${new Date().toISOString().slice(0, 10)}.csv`
                    );
                  }
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>

          {/* Key Performance Indicators */}
          <div className="grid md:grid-cols-4 gap-6">
            {topKPIs.length > 0 ? (
              topKPIs.map((kpi, idx) => (
                <Card key={idx}>
                  <CardHeader className="pb-2">
                    <CardDescription>{kpi.label}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end justify-between">
                      <div className="text-2xl font-bold">{kpi.value}</div>
                      <div className="flex items-center gap-1">
                        {kpi.trend === "up" ? (
                          <ArrowUpRight
                            className={`h-4 w-4 ${getStatusColor(kpi.status)}`}
                          />
                        ) : kpi.trend === "down" ? (
                          <ArrowDownRight
                            className={`h-4 w-4 ${getStatusColor(kpi.status)}`}
                          />
                        ) : null}
                        {kpi.change !== 0 && (
                          <span
                            className={`text-sm ${kpi.change >= 0 ? "text-success" : "text-destructive"}`}
                          >
                            {kpi.change >= 0 ? "+" : ""}
                            {kpi.change}%
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              // Fallback if no KPIs available
              <Card className="col-span-4">
                <CardContent className="py-8 text-center text-muted-foreground">
                  <Calculator className="h-12 w-12 mx-auto mb-4" />
                  <p>No transaction data available for this period.</p>
                  <p className="text-sm mt-2">
                    Import transactions to see financial KPIs.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
              <TabsTrigger value="expenses">Expenses</TabsTrigger>
              <TabsTrigger value="alerts">
                Alerts
                {alerts.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {alerts.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6 mt-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Financial Health Score */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle
                        className={`h-5 w-5 ${getHealthColor(healthStatus)}`}
                      />
                      Financial Health Score
                    </CardTitle>
                    <CardDescription>
                      Overall financial health assessment
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-center">
                      <div className="relative w-32 h-32">
                        <svg className="w-full h-full" viewBox="0 0 100 100">
                          <circle
                            className="text-muted stroke-current"
                            strokeWidth="10"
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                          />
                          <circle
                            className={`${getHealthColor(healthStatus)} stroke-current`}
                            strokeWidth="10"
                            strokeLinecap="round"
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            strokeDasharray={`${healthScore * 2.51} ${100 * 2.51}`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-3xl font-bold">
                            {healthScore}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-center">
                      <span
                        className={`text-lg font-semibold capitalize ${getHealthColor(healthStatus)}`}
                      >
                        {healthStatus}
                      </span>
                    </div>
                    <div className="text-center text-sm text-muted-foreground">
                      Based on liquidity, profitability, and solvency metrics
                    </div>
                    {/* Category breakdown */}
                    <div className="grid grid-cols-3 gap-4 text-center pt-4 border-t">
                      {kpisByCategory.liquidity && (
                        <div>
                          <div
                            className={`text-lg font-semibold ${getStatusColor(kpisByCategory.liquidity[0]?.status || "good")}`}
                          >
                            {kpisByCategory.liquidity[0]?.status === "good"
                              ? "Good"
                              : kpisByCategory.liquidity[0]?.status ===
                                  "warning"
                                ? "Fair"
                                : "Poor"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Liquidity
                          </div>
                        </div>
                      )}
                      {kpisByCategory.profitability && (
                        <div>
                          <div
                            className={`text-lg font-semibold ${getStatusColor(kpisByCategory.profitability[0]?.status || "good")}`}
                          >
                            {kpisByCategory.profitability[0]?.status === "good"
                              ? "Good"
                              : kpisByCategory.profitability[0]?.status ===
                                  "warning"
                                ? "Fair"
                                : "Poor"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Profitability
                          </div>
                        </div>
                      )}
                      {kpisByCategory.leverage && (
                        <div>
                          <div
                            className={`text-lg font-semibold ${getStatusColor(kpisByCategory.leverage[0]?.status || "good")}`}
                          >
                            {kpisByCategory.leverage[0]?.status === "good"
                              ? "Good"
                              : kpisByCategory.leverage[0]?.status === "warning"
                                ? "Fair"
                                : "Poor"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Solvency
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Summary Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Period Summary
                    </CardTitle>
                    <CardDescription>
                      Financial summary for selected period
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {summary ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between py-3 border-b">
                          <span className="text-muted-foreground">
                            Total Revenue
                          </span>
                          <span className="font-semibold text-success">
                            {formatChf(summary.totalRevenue * 100)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between py-3 border-b">
                          <span className="text-muted-foreground">
                            Total Expenses
                          </span>
                          <span className="font-semibold text-destructive">
                            {formatChf(summary.totalExpenses * 100)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between py-3">
                          <span className="text-muted-foreground font-medium">
                            Net Income
                          </span>
                          <span
                            className={`text-xl font-bold ${summary.netIncome >= 0 ? "text-success" : "text-destructive"}`}
                          >
                            {formatChf(summary.netIncome * 100)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center text-muted-foreground py-4">
                        No data available
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Revenue vs Expenses Chart */}
              {monthlyCashFlow.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Revenue vs Expenses
                    </CardTitle>
                    <CardDescription>
                      Monthly comparison of income and expenditure
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthlyCashFlow}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            className="stroke-muted"
                          />
                          <XAxis
                            dataKey="month"
                            tick={{
                              fill: "hsl(var(--muted-foreground))",
                              fontSize: 12,
                            }}
                            axisLine={{ stroke: "hsl(var(--border))" }}
                          />
                          <YAxis
                            tick={{
                              fill: "hsl(var(--muted-foreground))",
                              fontSize: 12,
                            }}
                            axisLine={{ stroke: "hsl(var(--border))" }}
                            tickFormatter={value =>
                              `${Math.round(value / 1000)}k`
                            }
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar
                            dataKey="revenue"
                            name="Revenue"
                            fill="hsl(var(--chart-1))"
                            radius={[4, 4, 0, 0]}
                          />
                          <Bar
                            dataKey="expenses"
                            name="Expenses"
                            fill="hsl(var(--chart-2))"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* All KPIs by Category */}
              {Object.entries(kpisByCategory).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="h-5 w-5" />
                      Detailed KPIs
                    </CardTitle>
                    <CardDescription>
                      All financial metrics by category
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {Object.entries(kpisByCategory).map(
                        ([category, kpis]) => (
                          <div key={category} className="space-y-3">
                            <h4 className="font-semibold capitalize border-b pb-2">
                              {category.replace("_", " ")}
                            </h4>
                            {kpis.map((kpi, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between text-sm"
                              >
                                <span className="text-muted-foreground">
                                  {kpi.nameGerman || kpi.name}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`font-medium ${getStatusColor(kpi.status)}`}
                                  >
                                    {kpi.format}
                                  </span>
                                  {kpi.benchmarkComparison && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      {kpi.benchmarkComparison === "above"
                                        ? "Above"
                                        : kpi.benchmarkComparison === "below"
                                          ? "Below"
                                          : "At"}{" "}
                                      avg
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="cashflow" className="space-y-6 mt-6">
              {/* Cash Flow Trend Chart */}
              {monthlyCashFlow.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Cash Flow Trend
                    </CardTitle>
                    <CardDescription>Net cash flow over time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={monthlyCashFlow}>
                          <defs>
                            <linearGradient
                              id="colorCashFlow"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="hsl(var(--chart-1))"
                                stopOpacity={0.3}
                              />
                              <stop
                                offset="95%"
                                stopColor="hsl(var(--chart-1))"
                                stopOpacity={0}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            className="stroke-muted"
                          />
                          <XAxis
                            dataKey="month"
                            tick={{
                              fill: "hsl(var(--muted-foreground))",
                              fontSize: 12,
                            }}
                            axisLine={{ stroke: "hsl(var(--border))" }}
                          />
                          <YAxis
                            tick={{
                              fill: "hsl(var(--muted-foreground))",
                              fontSize: 12,
                            }}
                            axisLine={{ stroke: "hsl(var(--border))" }}
                            tickFormatter={value =>
                              `${Math.round(value / 1000)}k`
                            }
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Area
                            type="monotone"
                            dataKey="netCashFlow"
                            name="Net Cash Flow"
                            stroke="hsl(var(--chart-1))"
                            fill="url(#colorCashFlow)"
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Liquidity KPIs */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Banknote className="h-5 w-5" />
                    Liquidity Metrics
                  </CardTitle>
                  <CardDescription>
                    Cash position and liquidity ratios
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {kpisByCategory.liquidity ? (
                    <div className="space-y-4">
                      {kpisByCategory.liquidity.map((kpi, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between py-3 border-b last:border-0"
                        >
                          <div>
                            <div className="font-medium">
                              {kpi.nameGerman || kpi.name}
                            </div>
                            {kpi.explanation && (
                              <div className="text-sm text-muted-foreground">
                                {kpi.explanation}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div
                              className={`text-lg font-bold ${getStatusColor(kpi.status)}`}
                            >
                              {kpi.format}
                            </div>
                            {kpi.industryBenchmark && (
                              <div className="text-xs text-muted-foreground">
                                Industry: {kpi.industryBenchmark}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center text-muted-foreground">
                      <BarChart3 className="h-12 w-12 mx-auto mb-4" />
                      <p>No cash flow data available</p>
                      <p className="text-sm mt-2">
                        Import transactions to see cash flow analysis
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="expenses" className="space-y-6 mt-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Expense Breakdown Pie Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="h-5 w-5" />
                      Expense Breakdown
                    </CardTitle>
                    <CardDescription>
                      Distribution of expenses by category
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {expenseBreakdown.length > 0 ? (
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPieChart>
                            <Pie
                              data={expenseBreakdown}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={renderCustomizedLabel}
                              outerRadius={100}
                              fill="hsl(var(--chart-1))"
                              dataKey="value"
                            >
                              {expenseBreakdown.map((_, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    CHART_COLORS[index % CHART_COLORS.length]
                                  }
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value: number | undefined) =>
                                value != null ? formatChf(value * 100) : ""
                              }
                            />
                            <Legend />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                        <div className="text-center">
                          <PieChart className="h-12 w-12 mx-auto mb-4" />
                          <p>No expense data available</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Expense Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Expense Details
                    </CardTitle>
                    <CardDescription>Breakdown by expense type</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {expenseBreakdown.length > 0 ? (
                      <div className="space-y-4">
                        {expenseBreakdown.map((expense, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between py-2 border-b last:border-0"
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{
                                  backgroundColor:
                                    CHART_COLORS[idx % CHART_COLORS.length],
                                }}
                              />
                              <span className="font-medium">
                                {expense.name}
                              </span>
                            </div>
                            <span className="font-semibold">
                              {formatChf(expense.value * 100)}
                            </span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between py-2 pt-4 border-t font-bold">
                          <span>Total Expenses</span>
                          <span>
                            {formatChf(
                              expenseBreakdown.reduce(
                                (sum, e) => sum + e.value,
                                0
                              ) * 100
                            )}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="py-8 text-center text-muted-foreground">
                        <CreditCard className="h-12 w-12 mx-auto mb-4" />
                        <p>No expense data available</p>
                        <p className="text-sm mt-2">
                          Import transactions to see expense analysis
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Efficiency KPIs */}
              {kpisByCategory.efficiency && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calculator className="h-5 w-5" />
                      Efficiency Metrics
                    </CardTitle>
                    <CardDescription>
                      Operating efficiency and cost management
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {kpisByCategory.efficiency.map((kpi, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between py-3 px-4 bg-muted/50 rounded-lg"
                        >
                          <div>
                            <div className="font-medium">
                              {kpi.nameGerman || kpi.name}
                            </div>
                            {kpi.explanation && (
                              <div className="text-xs text-muted-foreground">
                                {kpi.explanation}
                              </div>
                            )}
                          </div>
                          <div
                            className={`text-lg font-bold ${getStatusColor(kpi.status)}`}
                          >
                            {kpi.format}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="alerts" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Financial Alerts
                  </CardTitle>
                  <CardDescription>
                    Important notifications requiring attention
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {alerts.length > 0 ? (
                    <div className="space-y-4">
                      {alerts.map((alert, idx) => (
                        <div
                          key={idx}
                          className={`flex items-start gap-4 p-4 border rounded-lg ${
                            alert.severity === "critical"
                              ? "bg-destructive/10 border-destructive/30"
                              : alert.severity === "warning"
                                ? "bg-warning/10 border-warning/30"
                                : alert.severity === "positive"
                                  ? "bg-success/10 border-success/30"
                                  : ""
                          }`}
                        >
                          <AlertIcon severity={alert.severity} />
                          <div className="flex-1">
                            <h4 className="font-medium">
                              {alert.titleGerman || alert.title}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              {alert.description}
                            </p>
                            {alert.recommendation && (
                              <p className="text-sm mt-2 text-primary">
                                {alert.recommendation}
                              </p>
                            )}
                          </div>
                          <Badge
                            variant={getAlertBadgeVariant(alert.severity)}
                            className="ml-auto"
                          >
                            {alert.severity === "critical"
                              ? "Critical"
                              : alert.severity === "warning"
                                ? "Warning"
                                : alert.severity === "positive"
                                  ? "Positive"
                                  : "Info"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center text-muted-foreground">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 text-success" />
                      <p>No alerts at this time</p>
                      <p className="text-sm mt-2">
                        Your financial metrics are within normal ranges
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </DashboardLayout>
  );
}
